import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { createUseStyles } from "react-jss";

import TextInput from "../inputs/TextInput";

export const ImageContext = createContext(() => {});

export const useImageSelector = (initial, filter = x => true) => {
  const [image, setImage] = useState(initial);
  const context = useContext(ImageContext);

  return [image, () => context(m => setImage(m ?? initial), filter), setImage];
};

const useStyles = createUseStyles({
  background: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100%",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    opacity: props => (props.show ? 1 : 0),
    transition: "opacity 0.2s ease",
    "pointer-events": props => (props.show ? "auto" : "none"),
    "z-index": 100,
  },
  dim: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    "background-color": "rgba(0, 0, 0, 0.7)",
    "z-index": -1,
  },
  loading: {
    color: "white",
  },
  container: {
    width: "90%",
    "max-width": 700,
    "background-color": "#313131",
    "padding-bottom": 15,
    "border-radius": 5,
    "box-sizing": "border-box",
    "& h1": {
      color: "white",
    },
    "& > *": {
      padding: [0, 15],
    },
  },
  images: {
    width: "100%",
    height: "50vh",
    overflow: "auto",
    "box-sizing": "border-box",
    "margin-top": 15,
    padding: [20, 0, 0, 20],
    position: "relative",
  },
  image: {
    width: "calc(50% - 20px)",
    height: 150,
    "border-radius": 10,
    "background-color": "#252525",
    "background-size": "cover",
    position: "relative",
    cursor: "pointer",
    float: "left",
    margin: [0, 20, 20, 0],

    "& p": {
      color: "white",
      margin: 0,
      position: "absolute",
      right: 10,
      bottom: 5,
      "text-shadow": "0 0 1px #000000, 0 0 1px #000000, 0 0 1px #000000",
      textAlign: "right",
    },
  },
  title: {
    color: "white",
    margin: 0,
    "font-size": 18,
  },
  message: {
    color: "white",
    margin: 0,
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
});

const ImageSelector = ({
  show,
  onClose,
  onSubmit,
  images,
  activeImage,
  imageFilter,
}) => {
  const [filteredImages, setFilteredImages] = useState([]);
  const [search, setSearch] = useState("");
  const classes = useStyles({ show });

  useEffect(() => {
    setFilteredImages(
      Object.values(images).filter(
        x =>
          imageFilter(x) &&
          x.id !== activeImage &&
          !x.parent &&
          (!search ||
            x.name.toLowerCase().includes(search.toLowerCase()) ||
            (x.tag && x.tag.toLowerCase().includes(search.toLowerCase()))),
      ),
    );
  }, [images, activeImage, search, imageFilter]);

  return (
    <div className={classes.background}>
      <div className={classes.dim} onClick={onClose} />
      <div className={classes.container}>
        <h1>Image Selector</h1>
        <TextInput label="Search" value={search} onChange={s => setSearch(s)} />
        <div className={classes.images}>
          {filteredImages.length > 0 ? (
            filteredImages.map(image => (
              <div
                className={classes.image}
                key={image.id}
                style={{ backgroundImage: `url("${image.urls.low}")` }}
                onClick={() => {
                  setSearch("");
                  onSubmit(image.id)
                }}
              >
                <p>{image.name}</p>
              </div>
            ))
          ) : (
            <p className={classes.message}>No images found</p>
          )}
        </div>
      </div>
    </div>
  );
};

const ImageSelectorProvider = ({ images, activeImage, children }) => {
  const [showImage, setShowImage] = useState(false);
  const [imageCallback, setImageCallback] = useState(() => () => {});
  const [imageFilter, setImageFilter] = useState(() => () => true);

  const selectImage = useCallback((callback, filter) => {
    setShowImage(true);
    setImageFilter(() => filter);
    setImageCallback(() => (...args) => {
      setShowImage(false);
      callback(...args);
    });
  }, []);

  return (
    <ImageContext.Provider value={selectImage}>
      <ImageSelector
        show={showImage}
        onSubmit={imageCallback}
        onClose={() => setShowImage(false)}
        images={images}
        activeImage={activeImage}
        imageFilter={imageFilter}
      />
      {children}
    </ImageContext.Provider>
  );
};

export default ImageSelectorProvider;
